import { Params } from '@angular/router';

import * as dayjs from 'dayjs';
import { Observable } from 'rxjs';
import { map, scan } from 'rxjs/operators';

// query param key needs to explicitly have `date` string in the name to get parsed correctly
// eg 'date', 'start_date', 'end_date', 'date_start', 'date_end'
// we should consider this key immutable since users might bookmark these links, any change might
// need to consider backward compatibility
const DATE_KEY = 'date';
const DATE_FORMAT = 'YYYY-MM-DD';

type Parser = {
  // if check returns true, the transform function will be called
  check: (key: string, value: any) => boolean;
  transform: (key: string, value: any) => Record<string, any>;
};

// order matters
const DESERIALIZERS: Parser[] = [
  // handle dates
  {
    check: (key, value) => {
      const isDateParam = key === DATE_KEY || key.endsWith(`_${DATE_KEY}`) || key.startsWith(`${DATE_KEY}_`);
      return isDateParam;
    },
    transform: (key, value) => ({ [key]: dayjs(value, DATE_FORMAT) }),
  },
  // parse catch all
  {
    check: (key, value) => true,
    transform: (key, value) => {
      try {
        return { [key]: JSON.parse(value) };
      } catch (e) {
        return { [key]: value };
      }
    },
  },
];

// order matters
const SERIALIZERS: Parser[] = [
  // strip any undefined values
  {
    // empty string is valid value so let it pass
    check: (key, value) => value === undefined || value === null,
    transform: () => ({}),
  },
  // handle dates
  {
    check: (key, value) => dayjs.isDayjs(value),
    transform: (key, value) => ({ [key]: value.format(DATE_FORMAT) }),
  },
  // handle strings
  {
    check: (key, value) => typeof value === 'string',
    transform: (key, value) => ({ [key]: value }),
  },
  // stringify for catch all
  {
    check: (key, value) => true,
    transform: (key, value) => {
      const stringified = JSON.stringify(value);
      return { [key]: stringified };
    },
  },
];

/**
 * Steps through an array of handlers
 * @param parsers
 * @returns
 */

const createParser = (parsers: Parser[]) => data => {
  const result: Record<string, any> = {};
  try {
    for (const [key, value] of Object.entries(data)) {
      const parser = parsers.find(p => p.check(key, value));
      const deserializedValue = (parser as Parser).transform(key, value);
      Object.assign(result, deserializedValue);
    }

    return result;
  } catch (e) {
    return result;
  }
};

/**
 * Custom serializer for query parameters to handle array/objects, dayjs dates
 */
export const serialize = (
  params: Params,
  opts?: {
    parsers?: Parser[];
  },
): Params => {
  if (!params) return {};

  const { parsers = SERIALIZERS } = opts || {};

  const parser = createParser(parsers);
  return parser(params);
};

/**
 * Custom deserializer for query parameters to handle array/objects, dayjs dates
 */
export const deserialize = (
  params: Params,
  opts?: {
    parsers?: Parser[];
  },
): Params => {
  if (!params) return {};
  const { parsers = DESERIALIZERS } = opts || {};

  const parser = createParser(parsers);
  return parser(params);
};

/**
 * Compares previous and current query parameters and returns an object with param keys that have changed values
 */
const getDirtyParams = <T extends Params>(previous: T, current: T): Record<keyof T, boolean> => {
  const dirtyParams: Record<keyof T, boolean> = {} as Record<keyof T, boolean>;

  for (const key in current) {
    if (JSON.stringify(current[key]) !== JSON.stringify(previous[key])) {
      dirtyParams[key] = true;
    }
  }

  // check for removed keys
  for (const key in previous) {
    if (!(key in current)) {
      dirtyParams[key] = true;
    }
  }

  return dirtyParams;
};

/**
 * Custom operator to track dirty query parameters
 */
export const withDirtyParams = <T extends Params>() => {
  return (source$: Observable<T>) =>
    source$.pipe(
      scan(
        (acc, current) => {
          const dirty = getDirtyParams(acc.previous, current);
          return { previous: current, current, dirty };
        },
        {
          previous: {} as T,
          current: {} as T,
          dirty: {} as Record<keyof T, boolean>,
        },
      ),
      map(({ current, dirty }) => ({ params: current, dirty })),
    );
};
